import { useEffect, useRef, useState } from "react";
import { ArrowRight, Backpack, CalendarDays, Infinity as InfinityIcon, Snowflake, Sun } from "lucide-react";
import { Button, Section, SectionHeading } from "@/components/ui";
import { SEASONS } from "@/data/home";

const SEASON_ICONS = {
  weekends: CalendarDays,
  breaks: Backpack,
  summers: Sun,
  winters: Snowflake,
  year: InfinityIcon,
};

/** Full literals so Tailwind sees every class. The open panel is a white card with a progress bar. */
const PANEL_STYLE = {
  on: "border-brand-300 bg-white text-ink shadow-soft",
  off: "border-brand-200 bg-white/70 text-ink hover:border-brand-500 hover:bg-white",
  iconOn: "bg-brand-500 text-white",
  iconOff: "bg-brand-100 text-brand-500",
  bar: "bg-sun-400",
};

const AUTO_MS = 4200;

function prefersReducedMotion() {
  return typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * "All year round" as a row of panels (a stack on phones). The active one opens with its line; one CTA sits below the row.
 * It moves on by itself every few seconds while on screen; a manual pick restarts the timer,
 * and hover or keyboard focus pauses it. Every season's text stays in the DOM (hidden attribute) for crawlers.
 */
export function SeasonsSection() {
  const [activeId, setActiveId] = useState(SEASONS.items[0].id);
  const [paused, setPaused] = useState(false);
  const [visible, setVisible] = useState(true);
  const rootRef = useRef(null);

  // Only auto-play while the section is on screen.
  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return undefined;
    const observer = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting), { threshold: 0.25 });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Move to the next panel every few seconds.
  useEffect(() => {
    if (paused || !visible || prefersReducedMotion()) return undefined;
    const id = window.setTimeout(() => {
      setActiveId((current) => {
        const index = SEASONS.items.findIndex((item) => item.id === current);
        return SEASONS.items[(index + 1) % SEASONS.items.length].id;
      });
    }, AUTO_MS);
    return () => window.clearTimeout(id);
  }, [activeId, paused, visible]);

  return (
    <Section tone="sunTint" labelledBy="seasons-title" testId="home-seasons">
      <div
        ref={rootRef}
        className="relative z-10"
        onPointerEnter={(e) => e.pointerType === "mouse" && setPaused(true)}
        onPointerLeave={(e) => e.pointerType === "mouse" && setPaused(false)}
        onFocus={(e) => e.target.matches(":focus-visible") && setPaused(true)}
        onBlur={() => setPaused(false)}
      >
        <SectionHeading id="seasons-title" title={SEASONS.title} highlight="all year round" intro={SEASONS.intro} />

        <ul className="mt-8 flex flex-col gap-3 lg:flex-row lg:items-stretch lg:gap-4" aria-label="Pick a time of year">
          {SEASONS.items.map((item) => {
            const style = PANEL_STYLE;
            const Icon = SEASON_ICONS[item.id] ?? CalendarDays;
            const on = item.id === activeId;
            return (
              <li
                key={item.id}
                className={`flex transition-[flex-grow] duration-500 ease-in-out ${on ? "lg:flex-[3.4]" : "lg:flex-1"}`}
              >
                <div
                  className={`relative flex h-full w-full min-h-0 flex-col overflow-hidden rounded-4xl border p-4 transition-colors duration-300 sm:p-5 lg:h-[18rem] ${
                    on ? `pb-7 ${style.on}` : style.off
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => setActiveId(item.id)}
                    aria-pressed={on}
                    data-testid={`season-${item.id}`}
                    className={`flex min-h-[44px] items-center gap-3 rounded-2xl text-left focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-ink lg:flex-col lg:gap-5 ${
                      on ? "lg:items-start" : "lg:flex-1 lg:items-center lg:justify-center"
                    }`}
                  >
                    <span
                      aria-hidden="true"
                      className={`inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full transition-transform duration-300 ${
                        on ? `${style.iconOn} scale-110` : style.iconOff
                      }`}
                    >
                      <Icon className="h-5 w-5" />
                    </span>
                    {/* Open panel: normal text. Closed panels on desktop: the name runs up the side. */}
                    <span
                      className={`font-heading text-lg font-bold leading-tight sm:text-xl ${
                        on ? "" : "lg:rotate-180 lg:text-2xl lg:[writing-mode:vertical-rl]"
                      }`}
                    >
                      {item.name}
                    </span>
                  </button>

                  <div hidden={!on} className={`animate-pop mt-4 min-h-0 flex-1 lg:mt-7 ${on ? "block" : "hidden"}`}>
                    <p className="font-heading text-2xl font-bold leading-snug sm:text-3xl lg:text-4xl">{item.line}</p>
                  </div>

                  {/* Countdown to the next panel */}
                  {on ? (
                    <span
                      key={`${item.id}-bar`}
                      aria-hidden="true"
                      className={`absolute inset-x-0 bottom-0 h-1.5 origin-left animate-fill ${style.bar}`}
                      style={{ animationDuration: `${AUTO_MS}ms`, animationPlayState: paused ? "paused" : "running" }}
                    />
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>

        <div className="mt-8 flex justify-center sm:mt-10">
          <Button to={SEASONS.cta.to} data-testid="seasons-cta">
            {SEASONS.cta.label}
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      </div>
    </Section>
  );
}
